
import { HelpTip } from '../../ui/HelpTip';
import { NotionSelect } from '../../ui/notion/NotionSelect';
import { SwitchRow } from '../../shared/activity-drawer/ui/SwitchRow';
import { Calendar } from 'lucide-react';
import { EXTERNAL_LABELS, levelOptions, effectiveLevel, type ExternalLevel, type ExternalMax } from '../../../lib/calendar/profile/externalVisibility';

export interface ExternalCalendarRow {
  key: string;            // "google:<id>" | "microsoft:<id>"
  name: string;
  provider: 'Google' | 'Outlook';
  seen: boolean;
  receivesTasks: boolean;
}

interface Props {
  rows: ExternalCalendarRow[];
  max: ExternalMax | undefined;
  visibility: Record<string, ExternalLevel> | undefined;
  busy: string[];
  syncOffNote: boolean;
  onSeen: (row: ExternalCalendarRow, on: boolean) => void;
  onBusy: (row: ExternalCalendarRow, on: boolean) => void;
  onLevel: (row: ExternalCalendarRow, level: ExternalLevel) => void;
  onReceives: (row: ExternalCalendarRow) => void;
}

const HELP = {
  seen: { title: 'Lo vedo', text: 'Mostra gli eventi di questo calendario dentro Flusia, accanto alle tue task commerciali.', example: "Accendi 'Lavoro' per vedere le riunioni di Google nello stesso calendario di Flusia." },
  busy: { title: 'Mi rende occupata', text: 'Gli orari di questo calendario risultano occupati per i colleghi che cercano un momento libero. Il titolo resta privato, a meno che tu non lo mostri qui sotto.', example: "su Personale hai Dentista giovedì 16-17: con questo acceso, nessun collega può fissarti un cliente alle 16 senza vedere l'avviso; il titolo resta privato." },
  level: { title: 'I colleghi vedono', text: "Cosa vedono i colleghi degli eventi di questo calendario. Niente: nulla. Solo occupato: un blocco senza titolo. Titolo: il nome dell'evento. Tutti i dettagli: anche luogo e note. Le scelte in grigio le ha vietate l'amministratore.", example: "Con 'Solo occupato' un collega vede un blocco grigio giovedì 16-17; con 'Titolo' legge anche 'Dentista'." },
  receives: { title: 'Riceve le mie task', text: 'Il calendario dove Flusia scrive le tue task commerciali (videochiamate, appuntamenti). Ne scegli uno solo.', example: "Scegli 'Lavoro': l'appuntamento con Rossi di martedì alle 10 compare nel tuo Google Calendar Lavoro." },
};
const tip = (k: keyof typeof HELP) => <HelpTip {...HELP[k]} />;

/** I calendari esterni come righe piatte: un gruppo per calendario, con le sue quattro scelte. */
export function ExternalCalendarsTable({ rows, max, visibility, busy, syncOffNote, onSeen, onBusy, onLevel, onReceives }: Props) {
  if (!rows.length) return <p className="text-[13px] text-[#787774]">Nessun calendario esterno disponibile.</p>;
  return (
    <div className="flex flex-col">
      {rows.map(r => {
        const current = effectiveLevel(visibility?.[r.key], max);
        return (
          <section key={r.key} className="border-t border-[rgba(55,53,47,0.09)] py-2 first:border-t-0 dark:border-white/10">
            <div className="flex items-center gap-2 px-1.5 py-1">
              <Calendar className="h-4 w-4 shrink-0 text-[#787774]" strokeWidth={1.5} />
              <span className="min-w-0 truncate text-[13px] font-medium text-[#37352F] dark:text-white">{r.name}</span>
              <span className="text-[11px] text-[#787774]">{r.provider}</span>
            </div>
            <div className="flex flex-col gap-1.5 pl-6">
              <SwitchRow title="Lo vedo" help={tip('seen')} checked={r.seen} onChange={on => onSeen(r, on)} />
              <SwitchRow title="Mi rende occupata" help={tip('busy')} checked={busy.includes(r.key)} onChange={on => onBusy(r, on)} />
              <div className="flex items-center justify-between gap-2 px-1.5">
                <span className="flex items-center gap-1.5 text-xs font-medium text-[#37352F] dark:text-white">I colleghi vedono {tip('level')}</span>
                <NotionSelect
                  variant="flat"
                  aria-label={`I colleghi vedono: ${r.name}`}
                  value={current}
                  onChange={e => onLevel(r, e.target.value as ExternalLevel)}
                  options={levelOptions(max).map(o => ({
                    value: o.level,
                    label: `${EXTERNAL_LABELS[o.level]}${o.allowed ? '' : " (deciso dall'amministratore)"}`,
                    disabled: !o.allowed,
                  }))}
                />
              </div>
              <SwitchRow title="Riceve le mie task" help={tip('receives')} checked={r.receivesTasks} onChange={on => { if (on) onReceives(r); }} />
            </div>
          </section>
        );
      })}
      {syncOffNote && <p className="mt-1 px-1.5 text-[11px] text-[#787774]">Per scrivere le task sul calendario scelto, accendi anche "Sincronizza attività" nella riga di collegamento qui sopra.</p>}
    </div>
  );
}
